// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { sizeVariantValues } from "@conklin/MobiusOverrides/FormField/formStyles";
import IconArrowLeft from "@conklin/XDL/Icons/IconArrowLeft";
import IconArrowRight from "@conklin/XDL/Icons/IconArrowRight";
import IconCalendar from "@conklin/XDL/Icons/IconCalendar";
import IconClose from "@conklin/XDL/Icons/IconClose";
import { fontConfig } from "@conklin/XDL/PreStyle/typography";
import { xBaseTheme } from "@conklin/XDL/types";
import { color } from "@conklin/XDL/Utilities";
import DatePickerPresentationPropsDefault from "@insite/mobius/DatePicker/presentationProps";
import { IconWrapper } from "@insite/mobius/Icon";
import getColor from "@insite/mobius/utilities/getColor";
import React from "react";
import { css } from "styled-components";

export default {
    defaultProps: {
        mergeCss: true,
        ...DatePickerPresentationPropsDefault,
        dateTimePickerProps: {
            calendarProps: {
                showNeighboringMonth: true,
                nextLabel: React.createElement(IconArrowRight),
                prevLabel: React.createElement(IconArrowLeft),
            },
        },
        clearIconProps: {
            color: "common.ui5",
            src: IconClose,
            size: 10,
            css: css`
                &&& {
                    padding: 0;
                }
            `,
        },
        calendarIconProps: {
            color: "common.ui5",
            src: IconCalendar,
            size: 15,
            css: css`
                &&& {
                    padding: 0;
                }
            `,
        },
        placeholders: {
            dayPlaceholder: "DD",
            monthPlaceholder: "MM",
            yearPlaceholder: "YYYY",
            hourPlaceholder: "hr",
            minutePlaceholder: "min",
        },
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
            datePicker: css`
                button:focus {
                    /* outline is added on span for border reasons */
                    outline: none;
                    span {
                        outline: none;
                    }
                }
                &&& > .react-datetime-picker {
                    height: ${({ _sizeVariant }: any) => (sizeVariantValues as any)[_sizeVariant].height}px;

                    &:not(:disabled) {
                        &:hover {
                            border: 1px solid ${color("common.ui9")};
                        }
                        &:focus-within {
                            border: 1px solid ${color("common.ui10")};
                        }
                        .react-datetime-picker__inputGroup {
                            display: flex;
                            align-items: center;
                            justify-content: flex-start;
                            padding: 0;

                            &__input {
                                font-size: 15px;
                                line-height: 17px;
                                font-weight: ${fontConfig.weight.regular};
                                transition: none;
                                &,
                                &:hover,
                                &:focus {
                                    border: none;
                                    box-shadow: none;
                                    padding: 0;

                                    &[name="year"][value=""] {
                                        width: 39px !important;
                                    }

                                    &[name="month"][value=""] {
                                        width: 29px !important;
                                    }

                                    &[name="day"][value=""] {
                                        width: 23px !important;
                                    }
                                }
                            }

                            &__leadingZero {
                                font-size: 15px;
                                line-height: 20px;
                            }
                        }
                        .react-datetime-picker__button {
                            ${IconWrapper} {
                                color: ${color("common.ui5")};

                                &:hover,
                                &:focus {
                                    color: ${color("common.ui10")};
                                }
                            }
                        }

                        .react-datetime-picker__clear-button {
                            ${IconWrapper} {
                                color: ${color("common.ui5")};

                                &:hover,
                                &:focus {
                                    color: ${color("common.ui10")};
                                }
                            }
                        }
                    }

                    /* when placeholder is shown, divider color is set to common.ui4 matching input placeholder color */
                    .react-datetime-picker__inputGroup:has(
                            .react-datetime-picker__inputGroup__input:placeholder-shown
                        ) {
                        .react-datetime-picker__inputGroup__divider {
                            color: ${getColor("common.ui4")};
                        }
                    }
                    &:not(:disabled),
                    &:focus-within {
                        .react-datetime-picker__button {
                            padding-left: 0;
                            padding-right: 0;
                            outline: none;
                        }

                        .react-datetime-picker__clear-button {
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            margin-left: 8px;
                            padding-right: 0;
                            outline: none;
                        }
                        .react-datetime-picker__calendar-button {
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            margin-left: 8px;
                            padding-right: 0;
                            outline: none;
                            order: 0;
                        }
                    }
                }
                .react-calendar {
                    width: 280px;
                    box-shadow: none;
                    margin-top: 0;
                    outline: ${getColor("common.ui5")} solid 1px;
                    padding: 15px 20px 20px 20px;
                    button {
                        font-family: inherit;
                        &:enabled {
                            &:hover:not(:disabled),
                            &:focus:not(:disabled) {
                                color: ${getColor("common.white")};
                                background-color: ${getColor("common.ui10")};
                            }
                        }
                    }

                    .react-calendar__navigation {
                        justify-content: space-between;
                        margin-bottom: 15px;

                        &__arrow {
                            height: auto;
                        }

                        &__prev-button,
                        &__next-button {
                            min-width: 0;
                            padding: 0;
                            width: auto;
                            color: ${getColor("common.ui4")};
                            background-image: none;
                            background: transparent;
                            &:enabled {
                                &:hover:not(:disabled),
                                &:focus:not(:disabled) {
                                    background: transparent;
                                }
                                &:focus:not(:disabled) {
                                    color: ${getColor("common.ui4")};
                                    outline: none;
                                }

                                &:hover:not(:disabled) {
                                    color: ${getColor("common.ui9")};
                                }
                            }

                            &:disabled {
                                color: ${getColor("common.ui4")};
                                cursor: not-allowed;
                                opacity: 0.33;
                            }

                            svg {
                                width: 8px;
                                height: 12px;
                            }
                        }

                        &__label {
                            font-size: 16px;
                            font-weight: 600;
                            line-height: 18px;
                            flex-grow: 0 !important;
                            width: auto;

                            &:enabled {
                                &:hover:not(:disabled),
                                &:focus:not(:disabled) {
                                    background: transparent;
                                }
                                &:focus:not(:disabled) {
                                    color: ${getColor("text")};
                                    outline: none;
                                }

                                &:hover:not(:disabled) {
                                    color: ${getColor("common.ui9")};
                                }
                            }
                        }
                    }

                    &__month-view {
                        &__weekdays {
                            font-size: 10px;
                            font-weight: normal;
                            color: ${getColor("text")};
                            letter-spacing: 0.4px;
                            text-transform: uppercase;
                            margin-bottom: 5px;
                        }

                        &__days {
                            &__day {
                                color: ${getColor("text")};
                                border-radius: 0;
                                font-weight: normal;
                                font-size: 12px;
                                height: 29px;
                                width: 29px;
                                padding: 0;

                                &.react-calendar__tile--now {
                                    position: relative;
                                    &:after {
                                        content: "";
                                        position: absolute;
                                        bottom: 3px;
                                        left: 50%;
                                        transform: translateX(-50%);
                                        color: ${getColor("common.ui10")};
                                        border: 1px solid ${getColor("common.ui10")};
                                        border-radius: 50%;
                                        font-size: 0;
                                    }
                                }

                                &:disabled {
                                    color: ${getColor("common.ui4")};
                                    cursor: not-allowed;
                                }

                                &--neighboringMonth {
                                    color: ${getColor("common.border")};
                                }
                            }
                        }
                    }

                    &__year-view,
                    &__decade-view,
                    &__century-view {
                        .react-calendar__tile {
                            color: ${getColor("text")};
                            padding: 5px 0;
                            font-weight: normal;
                            font-size: 12px;

                            &:disabled {
                                color: ${getColor("common.ui4")};
                                cursor: not-allowed;
                            }
                        }
                    }

                    &__tile {
                        &--hasActive:not(:disabled):not(.react-calendar__month-view__days__day--neighboringMonth),
                        &--active:not(:disabled):not(.react-calendar__month-view__days__day--neighboringMonth) {
                            background-color: ${getColor("common.ui10")};
                            &:after {
                                content: none;
                            }
                        }
                    }
                }
            `,
        },
    },
} as xBaseTheme["datePicker"];
